import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';


// Custom Node Component
const CustomNode = ({ data, selected }) => {
  return (
    <div className={`border-2 bg-white rounded-3 shadow-sm position-relative ${selected ? 'border-primary shadow' : 'border-secondary'}`} style={{ 
      width: 180, 
      background: 'white', 
      border: `2px solid ${selected ? '#3b82f6' : '#e5e7eb'}`, 
      borderRadius: 12, 
      boxShadow: selected ? '0 4px 16px rgba(59, 130, 246, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.1)', 
      position: 'relative',
      transition: 'all 0.2s'
    }}>
      {/* Input Handle - Top */}
      {data.type !== 'trigger' && (
        <Handle
          type="target"
          position={Position.Top}
          id="input"
          className="border-2 bg-white rounded-circle shadow-sm"
          style={{ 
            width: 12, 
            height: 12, 
            background: 'white', 
            border: '2px solid #6366f1', 
            borderRadius: '50%', 
            top: -6, 
            transition: 'all 0.2s' 
          }}
        />
      )}
      
      <div className="p-3" style={{ background: data.color, padding: '12px', borderRadius: '10px 10px 0 0' }}>
        <div className="d-flex align-items-center gap-2">
          <span className="fs-5">{data.icon}</span>
          <div className="text-white">
            <strong className="d-block">{data.title}</strong>
            <p className="mb-0 small opacity-90">{data.subtitle}</p>
          </div>
        </div>
      </div>
      {data.type === 'menu' && data.options && (
        <div className="px-3 py-2">
          <span className="badge bg-light text-dark small">{data.options.length} Options</span>
        </div>
      )}
      {data.type === 'menu' && <div className="px-3 pb-2"><span className="badge bg-success small">AI Handled</span></div>}
      {data.type === 'department' && data.title === 'Refills' && (
        <div className="px-3 pb-2"><span className="badge bg-success small">AI Handled</span></div>
      )}
      
      {/* Output Handle - Bottom */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="output"
        className="border-2 bg-white rounded-circle shadow-sm"
        style={{ 
          width: 12, 
          height: 12, 
          background: 'white', 
          border: '2px solid #6366f1', 
          borderRadius: '50%', 
          bottom: -6, 
          transition: 'all 0.2s' 
        }}
      />
    </div>
  );
};

const nodeTypes = {
  custom: CustomNode,
};

const initialNodes = [
  {
    id: 'start-call',
    type: 'custom',
    position: { x: 360, y: 100 },
    data: {
      type: 'trigger',
      title: 'Start Call',
      subtitle: 'Incoming call detected',
      icon: '▶️',
      color: '#3b82f6'
    }
  }
];

const initialEdges = [];

const PhoneTreeEditor = () => {
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);

  const componentLibrary = [
    {
      type: 'greeting',
      icon: '🎤',
      title: 'Greeting',
      subtitle: 'Welcome message & intro',
      color: '#3b82f6'
    },
    {
      type: 'menu',
      icon: '☰',
      title: 'Menu',
      subtitle: 'Interactive menu options',
      color: '#10b981'
    },
    {
      type: 'department',
      icon: '🏢',
      title: 'Department',
      subtitle: 'Route to specific dept',
      color: '#8b5cf6'
    },
    {
      type: 'after-hours',
      icon: '🌙',
      title: 'After Hours',
      subtitle: 'Off-hours routing',
      color: '#f97316'
    },
    {
      type: 'transfer',
      icon: '📞',
      title: 'Transfer',
      subtitle: 'Transfer to extension',
      color: '#ef4444'
    },
    {
      type: 'voicemail',
      icon: '💬',
      title: 'Voicemail',
      subtitle: 'Record message',
      color: '#6b7280'
    }
  ];

  const quickTemplates = [
    { id: 'new-patient', label: 'New Patient', icon: '👤', color: '#3b82f6' },
    { id: 'appointment', label: 'Appointment Booking', icon: '📅', color: '#10b981' },
    { id: 'refills', label: 'Refills', icon: '💊', color: '#8b5cf6' }
  ];

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ 
      ...params, 
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#6366f1', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' }
    }, eds)),
    [setEdges]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const componentData = event.dataTransfer.getData('application/reactflow');
      if (!componentData) return;

      const component = JSON.parse(componentData);
      const position = {
        x: event.clientX - event.currentTarget.getBoundingClientRect().left - 90,
        y: event.clientY - event.currentTarget.getBoundingClientRect().top - 40,
      };

      const newNode = {
        id: `${component.type}-${Date.now()}`,
        type: 'custom',
        position,
        data: {
          type: component.type,
          title: component.title,
          subtitle: component.subtitle,
          icon: component.icon,
          color: component.color,
          options: component.type === 'menu' ? ['Appointments', 'Refills', 'Billing', 'Representative'] : []
        }
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [setNodes]
  );

  const onDragStart = (event, component) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify(component));
    event.dataTransfer.effectAllowed = 'move';
  };

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const handleDeleteNode = useCallback(() => {
    if (selectedNode) {
      setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
      setEdges((eds) => eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
      setSelectedNode(null);
    }
  }, [selectedNode, setNodes, setEdges]);

  const handleUpdateNode = (updates) => {
    if (selectedNode) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === selectedNode.id
            ? { ...node, data: { ...node.data, ...updates } }
            : node
        )
      );
      setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, ...updates } });
    }
  };

  const handleAddMenuOption = () => {
    if (selectedNode && selectedNode.data.type === 'menu') {
      const newOptions = [...(selectedNode.data.options || []), 'New Option'];
      handleUpdateNode({ options: newOptions });
    }
  };

  const handleUpdateMenuOption = (index, value) => {
    if (selectedNode && selectedNode.data.type === 'menu') {
      const newOptions = [...selectedNode.data.options];
      newOptions[index] = value;
      handleUpdateNode({ options: newOptions });
    }
  };

  const handleDeleteMenuOption = (index) => {
    if (selectedNode && selectedNode.data.type === 'menu') {
      const newOptions = selectedNode.data.options.filter((_, i) => i !== index);
      handleUpdateNode({ options: newOptions });
    }
  };

  const handleSaveFlow = () => {
    console.log('Saving flow:', { nodes, edges });
    alert('Flow saved successfully!');
  };

  const handleLoadTemplate = (templateId) => {
    let templateNodes = [];
    let templateEdges = [];
    
    if (templateId === 'new-patient') {
      templateNodes = [
        { 
          id: 'start', 
          type: 'custom', 
          position: { x: 360, y: 50 }, 
          data: { type: 'trigger', title: 'Start Call', subtitle: 'Incoming call detected', icon: '▶️', color: '#3b82f6' }
        },
        { 
          id: 'greeting', 
          type: 'custom', 
          position: { x: 360, y: 180 }, 
          data: { type: 'greeting', title: 'Greeting', subtitle: 'Welcome to our clinic', icon: '🎤', color: '#3b82f6' }
        },
        { 
          id: 'menu', 
          type: 'custom', 
          position: { x: 360, y: 310 }, 
          data: { type: 'menu', title: 'Main Menu', subtitle: 'Press 1-4 for options', icon: '☰', color: '#10b981', options: ['New Patient', 'Existing Patient', 'Billing', 'Representative'] }
        }
      ];
      templateEdges = [
        { id: 'e1-2', source: 'start', target: 'greeting', type: 'smoothstep', animated: true, style: { stroke: '#6366f1', strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' } },
        { id: 'e2-3', source: 'greeting', target: 'menu', type: 'smoothstep', animated: true, style: { stroke: '#6366f1', strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' } }
      ];
    } else if (templateId === 'appointment') {
      templateNodes = [
        { 
          id: 'start', 
          type: 'custom', 
          position: { x: 360, y: 50 }, 
          data: { type: 'trigger', title: 'Start Call', subtitle: 'Incoming call detected', icon: '▶️', color: '#3b82f6' }
        },
        { 
          id: 'greeting', 
          type: 'custom', 
          position: { x: 360, y: 180 }, 
          data: { type: 'greeting', title: 'Greeting', subtitle: 'Appointment booking', icon: '🎤', color: '#3b82f6' }
        },
        { 
          id: 'menu', 
          type: 'custom', 
          position: { x: 360, y: 310 }, 
          data: { type: 'menu', title: 'Appointments', subtitle: 'Book or modify', icon: '☰', color: '#10b981', options: ['Book Appointment', 'Reschedule', 'Cancel', 'Speak to Staff'] }
        }
      ];
      templateEdges = [
        { id: 'e1-2', source: 'start', target: 'greeting', type: 'smoothstep', animated: true, style: { stroke: '#6366f1', strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' } },
        { id: 'e2-3', source: 'greeting', target: 'menu', type: 'smoothstep', animated: true, style: { stroke: '#6366f1', strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' } }
      ];
    } else if (templateId === 'refills') {
      templateNodes = [
        { 
          id: 'start', 
          type: 'custom', 
          position: { x: 360, y: 50 }, 
          data: { type: 'trigger', title: 'Start Call', subtitle: 'Incoming call detected', icon: '▶️', color: '#3b82f6' }
        },
        { 
          id: 'greeting', 
          type: 'custom', 
          position: { x: 360, y: 180 }, 
          data: { type: 'greeting', title: 'Greeting', subtitle: 'Prescription refills', icon: '🎤', color: '#8b5cf6' }
        },
        { 
          id: 'refills', 
          type: 'custom', 
          position: { x: 360, y: 310 }, 
          data: { type: 'department', title: 'Refills', subtitle: 'Request medication', icon: '🏢', color: '#8b5cf6' }
        }
      ];
      templateEdges = [
        { id: 'e1-2', source: 'start', target: 'greeting', type: 'smoothstep', animated: true, style: { stroke: '#6366f1', strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' } },
        { id: 'e2-3', source: 'greeting', target: 'refills', type: 'smoothstep', animated: true, style: { stroke: '#6366f1', strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' } }
      ];
    }
    
    setNodes(templateNodes);
    setEdges(templateEdges);
    setSelectedNode(null);
  };

  return (
    <div className="min-vh-100 bg-light" style={{ minHeight: '100vh', background: '#f8f9fc', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif' }}>
      {/* Header */}
      <div className="bg-white border-bottom" style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '12px 24px' }}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-4">
            <h1 className="h4 mb-0 text-primary" style={{ fontSize: 20, fontWeight: 700, color: '#3b82f6', margin: 0 }}>Ankr</h1>
            <nav className="d-flex gap-2">
              <button 
                className="btn btn-link text-decoration-none px-3 py-2 rounded" 
                style={{ padding: '8px 16px', background: 'transparent', border: 'none', color: '#6b7280', fontSize: 14, transition: 'all 0.2s' }}
                onClick={() => navigate('/dashboard')}
                onMouseEnter={(e) => { e.target.style.background = '#f3f4f6'; e.target.style.color = '#1a1a2e'; }}
                onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.color = '#6b7280'; }}
              >
                Dashboard
              </button>
              <button 
                className="btn btn-link text-decoration-none px-3 py-2 rounded" 
                style={{ padding: '8px 16px', background: 'transparent', border: 'none', color: '#6b7280', fontSize: 14, transition: 'all 0.2s' }}
                onClick={() => navigate('/onboarding/ehr-connect')}
                onMouseEnter={(e) => { e.target.style.background = '#f3f4f6'; e.target.style.color = '#1a1a2e'; }}
                onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.color = '#6b7280'; }}
              >
                EHR Connect
              </button>
              <button 
                className="btn btn-link text-decoration-none px-3 py-2 rounded" 
                style={{ padding: '8px 16px', background: 'transparent', border: 'none', color: '#6b7280', fontSize: 14, transition: 'all 0.2s' }}
                onClick={() => navigate('/onboarding/phone-setup')}
                onMouseEnter={(e) => { e.target.style.background = '#f3f4f6'; e.target.style.color = '#1a1a2e'; }}
                onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.color = '#6b7280'; }}
              >
                Phone Setup
              </button>
              <button 
                className="btn btn-link text-decoration-none px-3 py-2 rounded active" 
                style={{ padding: '8px 16px', background: '#eff6ff', color: '#3b82f6', fontSize: 14, fontWeight: 600 }}
              >
                Phone Tree Editor
              </button>
              <button 
                className="btn btn-link text-decoration-none px-3 py-2 rounded" 
                style={{ padding: '8px 16px', background: 'transparent', border: 'none', color: '#6b7280', fontSize: 14, transition: 'all 0.2s' }}
                onClick={() => navigate('/settings')}
                onMouseEnter={(e) => { e.target.style.background = '#f3f4f6'; e.target.style.color = '#1a1a2e'; }}
                onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.color = '#6b7280'; }}
              >
                Settings
              </button>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-3">
            <div className="badge bg-warning text-dark px-3 py-1" style={{ padding: '6px 12px', background: '#fef3c7', borderRadius: 20, fontSize: 13, fontWeight: 500, color: '#92400e' }}>🏆 320 XP</div>
            <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, borderRadius: '50%', background: '#f3f4f6', cursor: 'pointer' }}>🔔</div>
            <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, borderRadius: '50%', background: '#dbeafe', cursor: 'pointer' }}>👤</div>
          </div>
        </div>
      </div>

      <div className="d-grid" style={{ display: 'grid', gridTemplateColumns: '220px 1fr 280px', height: 'calc(100vh - 61px)' }}>
        {/* Component Library Sidebar */}
        <div className="bg-white border-end overflow-auto" style={{ background: 'white', borderRight: '1px solid #e5e7eb', padding: '20px 16px', overflowY: 'auto' }}>
          <h3 className="h6 mb-1">Component Library</h3>
          <p className="text-muted small mb-4" style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 20px 0' }}>Drag components to the canvas</p>
          
          <div className="d-flex flex-column gap-2 mb-4" style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            {componentLibrary.map((component) => (
              <div
                key={component.type}
                className="d-flex align-items-center gap-3 p-2 border rounded cursor-grab"
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 12, 
                  padding: '8px', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: 8, 
                  cursor: 'grab', 
                  transition: 'all 0.2s' 
                }}
                draggable
                onDragStart={(e) => onDragStart(e, component)}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f3f4f6';
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.transform = 'translateY(0)';
                }}
                onMouseDown={(e) => e.target.style.cursor = 'grabbing'}
                onMouseUp={(e) => e.target.style.cursor = 'grab'}
              >
                <div 
                  className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0" 
                  style={{ 
                    width: 36, 
                    height: 36, 
                    borderRadius: 8, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    flexShrink: 0, 
                    background: component.color, 
                    color: 'white', 
                    fontSize: 16 
                  }}
                >
                  {component.icon}
                </div>
                <div className="flex-grow-1 min-w-0">
                  <strong className="d-block small" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1a1a2e', marginBottom: 2 }}>{component.title}</strong>
                  <p className="mb-0 small text-muted" style={{ fontSize: 11, color: '#9ca3af', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{component.subtitle}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="d-flex gap-2 p-3 bg-warning bg-opacity-25 rounded-2" style={{ 
            display: 'flex', 
            gap: 10, 
            padding: 12, 
            background: 'rgba(245, 158, 11, 0.1)', 
            border: '1px solid #f59e0b', 
            borderRadius: 8, 
            marginTop: 20 
          }}>
            <span className="fs-5 flex-shrink-0" style={{ fontSize: 18, flexShrink: 0 }}>💡</span>
            <div>
              <strong className="d-block small" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 2 }}>Pro Tip</strong>
              <p className="mb-0 small" style={{ fontSize: 11, color: '#78350f', margin: 0 }}>Start with a template and customize it to save time!</p>
            </div>
          </div>
        </div>

        {/* Canvas Area */}
        <div className="bg-light d-flex flex-column overflow-hidden" style={{ background: '#f8f9fc', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div className="bg-white border-bottom p-4" style={{ padding: '20px 24px', background: 'white', borderBottom: '1px solid #e5e7eb' }}>
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h2 className="h4 mb-1" style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', margin: '0 0 4px 0' }}>Phone Tree Visual Editor</h2>
                <p className="text-muted mb-0" style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>Design your automated call flow</p>
              </div>
              <div className="d-flex gap-2">
                <button className="btn btn-sm" style={{ padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer', border: 'none', background: '#eff6ff', color: '#3b82f6' }} onMouseEnter={(e) => e.target.style.background = '#dbeafe'} onMouseLeave={(e) => e.target.style.background = '#eff6ff'}>
                  <span>🎓</span> Tutorial
                </button>
                <button 
                  className="btn btn-sm btn-primary" 
                  onClick={handleSaveFlow}
                  style={{ padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer', border: 'none', background: '#3b82f6', color: 'white' }}
                  onMouseEnter={(e) => e.target.style.background = '#2563eb'}
                  onMouseLeave={(e) => e.target.style.background = '#3b82f6'}
                >
                  <span>💾</span> Save Flow
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white border-bottom p-3 d-flex gap-2" style={{ padding: '16px 24px', background: 'white', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 10 }}>
            <span className="small fw-semibold text-muted" style={{ fontSize: 13, fontWeight: 600, color: '#6b7280' }}>Quick Templates:</span>
            {quickTemplates.map((template) => (
              <button
                key={template.id}
                className="btn btn-sm text-white d-flex align-items-center gap-1"
                style={{ 
                  padding: '8px 14px', 
                  borderRadius: 6, 
                  fontSize: 12, 
                  fontWeight: 500, 
                  cursor: 'pointer', 
                  border: 'none', 
                  background: template.color, 
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 6,
                  transition: 'all 0.2s'
                }}
                onClick={() => handleLoadTemplate(template.id)}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <span>{template.icon}</span> {template.label}
              </button>
            ))}
          </div>

          <div className="flex-grow-1" style={{ flex: 1, background: '#f8f9fc' }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onPaneClick={onPaneClick}
              onDrop={onDrop}
              onDragOver={onDragOver}
              nodeTypes={nodeTypes}
              fitView
            >
              <Background color="#e5e7eb" gap={20} />
              <Controls />
              <MiniMap 
                nodeColor={(node) => node.data.color}
                nodeStrokeWidth={3}
                zoomable
                pannable
              />
            </ReactFlow>
          </div>
        </div>

        {/* Properties Panel */}
        <div className="bg-white border-start overflow-auto" style={{ background: 'white', borderLeft: '1px solid #e5e7eb', padding: '20px 16px', overflowY: 'auto' }}>
          <h3 className="h6 mb-1">Properties</h3>
          <p className="text-muted small mb-4" style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 20px 0' }}>
            {selectedNode ? 'Configure selected node' : 'Select a node to configure'}
          </p>

          {selectedNode && (
            <>
              <div className="mb-4">
                <label className="form-label small fw-semibold">Node Type</label>
                <div className="d-flex align-items-center gap-2 p-2 bg-light rounded">
                  <div 
                    className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0" 
                    style={{ 
                      width: 36, 
                      height: 36, 
                      borderRadius: 8, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      flexShrink: 0, 
                      background: selectedNode.data.color, 
                      color: 'white', 
                      fontSize: 16 
                    }}
                  >
                    {selectedNode.data.icon}
                  </div>
                  <span className="fw-medium">{selectedNode.data.title}</span>
                </div>
              </div>

              <div className="mb-4">
                <button className="btn btn-sm btn-danger w-100" onClick={handleDeleteNode} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer', border: 'none', background: '#ef4444', color: 'white', borderRadius: 6 }}>
                  🗑️ Delete Node
                </button>
              </div>

              {selectedNode.data.type === 'greeting' && (
                <div className="mb-4">
                  <label className="form-label small fw-semibold">Audio Message</label>
                  <textarea
                    className="form-control mb-2"
                    placeholder="Welcome to our clinic. For appointments, press 1. For prescription refills, press 2. For billing..."
                    rows={4}
                    style={{ fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 6 }}
                  />
                  <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-outline-secondary" style={{ padding: '6px 12px', fontSize: 12, border: '1px solid #e5e7eb', background: 'white', color: '#6b7280' }}>🎙️ Record</button>
                    <button className="btn btn-sm btn-outline-primary" style={{ padding: '6px 12px', fontSize: 12, border: '1px solid #3b82f6', background: 'white', color: '#3b82f6' }}>▶️ Preview</button>
                  </div>
                </div>
              )}

              {selectedNode.data.type === 'menu' && (
                <div className="mb-4">
                  <label className="form-label small fw-semibold">Menu Options</label>
                  <div className="d-flex flex-column gap-2">
                    {selectedNode.data.options?.map((option, index) => (
                      <div key={index} className="d-flex align-items-center gap-2 p-2 bg-light rounded">
                        <span className="badge bg-secondary flex-shrink-0" style={{ fontSize: 12, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4 }}>{index + 1}</span>
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => handleUpdateMenuOption(index, e.target.value)}
                          className="form-control form-control-sm flex-grow-1"
                          style={{ fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 4 }}
                        />
                        <button
                          className="btn btn-sm btn-outline-secondary flex-shrink-0"
                          onClick={() => handleDeleteMenuOption(index)}
                          style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #e5e7eb', background: 'white', color: '#6b7280' }}
                        >
                          ⋮
                        </button>
                      </div>
                    ))}
                  </div>
                  <button className="btn btn-sm btn-outline-primary w-100 mt-2" onClick={handleAddMenuOption} style={{ padding: '8px 16px', fontSize: 13, border: '1px solid #3b82f6', background: 'white', color: '#3b82f6' }}>
                    + Add Option
                  </button>
                </div>
              )}

              <div className="mb-4">
                <label className="form-label small fw-semibold">Advanced Settings</label>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="small">Timeout (seconds)</span>
                  <input type="number" defaultValue="5" className="form-control form-control-sm" style={{ width: 80, fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 4 }} />
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="small">Max Retries</span>
                  <input type="number" defaultValue="3" className="form-control form-control-sm" style={{ width: 80, fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 4 }} />
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="small">Enable Voice Input</span>
                  <label className="form-check form-switch">
                    <input type="checkbox" className="form-check-input" defaultChecked style={{ width: 44, height: 24 }} />
                    <span className="form-check-label"></span>
                  </label>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhoneTreeEditor;
